import React, { useEffect, useMemo, useState } from "react";
import {
  IoIosCheckmarkCircleOutline,
  IoIosCloseCircleOutline,
} from "react-icons/io";
import { MdClose } from "react-icons/md";
import { toast } from "react-toastify";

import ContainedButton from "components/layout/form/containedButton";
import TextField from "components/layout/form/textField";
import api from "services/api";

import {
  AnswerForm,
  AttemptsLeft,
  Content,
  ContentButtons,
  ContentRight,
  Counter,
  Header,
  Paragraph,
  PopupContainer,
  PopupWrapper,
  QuestionTitle,
  Title,
} from "./styles";

const QuestionPool = ({
  body: { document, userId, siteId, access_token },
  handleModal,
  setLoading,
  errorStep,
  setQuestionsAsValid,
}: any): JSX.Element => {
  const [questionsPool, setQuestionsPool] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState("");
  const [currentQuestionId, setCurrentQuestionId] = useState("");
  const [questionCounter, setQuestionCounter] = useState(0);
  const [attemptsLeft, setAttemptsLeft] = useState(4);
  const [initialState, setInitialState] = useState(true);
  const [validatedQuestions, setValidatedQuestions] = useState(false);
  const [hasAttemptsLeft, setHasAttemptsLeft] = useState(true);
  const [answerInput, setAnswerInput] = useState("");
  const [answers, setAnswers] = useState([]);

  const idsToCheck = [
    "750d57f8-2b90-43ca-9ebd-49660dc56b5d",
    "7d50cd5d-30b4-4b78-bcdf-1c9dc7989c5f",
  ];

  const idState = ["14cb7687-901b-4e23-8752-da3696ef377f"];

  const hasCep = idsToCheck.some((id) => currentQuestionId === id);

  const isState = idState.some((id) => currentQuestionId === id);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = e.target;
    if (hasCep) {
      if (value.length <= 3 && /^\d*$/.test(value)) {
        setAnswerInput(value);
      }
    } else if (isState) {
      if (value.length <= 2 && /^\w*$/.test(value)) {
        setAnswerInput(value.toUpperCase());
      }
    } else {
      setAnswerInput(value.toUpperCase());
    }
  };

  useEffect(() => {
    if (questionsPool.length === 0) {
      getQuestions();
    }

    if (answers.length === 2) {
      sendAnswers();
    }
  }, [questionsPool, answers]);

  const getQuestions = async (): Promise<void> => {
    setLoading(true);

    try {
      const result = await api.get(
        `industrial/customer/v1/questions?quantity=2`,
        {
          headers: {
            access_token,
          },
        }
      );

      setQuestionsPool(result.data);
      setCurrentQuestion(result.data[0]?.question);
      setCurrentQuestionId(result.data[0]?.id);
    } catch (err) {
      setLoading(false);

      const erro = err.response;
      if (erro) {
        const { message } = err.response.data;
        if (err.response.status !== 401) {
          toast.error(message);
        }
      } else {
        toast.error("Ocorreu um erro interno na aplicação");
      }
      errorStep();
    }

    setLoading(false);
  };

  const nextQuestion = (): void => {
    if (answerInput?.length < 3 && hasCep) return;

    if (answerInput) {
      saveAnswers(answerInput, questionsPool[questionCounter].id);
    }

    if (answerInput && questionCounter < 1) {
      setCurrentQuestion(questionsPool[1].question);
      setCurrentQuestionId(questionsPool[1]?.id);
      setAnswerInput("");
      setQuestionCounter((prevState) => prevState + 1);
    }
  };

  const saveAnswers = (answer, questionId): void => {
    const dontHaveAnswerId = !answers.some(
      (answer) => answer.questionId === questionId
    );

    if (dontHaveAnswerId) {
      setAnswers((prevState) => [
        ...prevState,
        { questionId, answer: answer.trim() },
      ]);
    }
  };

  const sendAnswers = async (): Promise<void> => {
    setLoading(true);

    const body = {
      document,
      userId,
      siteId,
      answers,
    };

    const headers = {
      headers: {
        access_token,
        "Access-Control-Expose-Headers": "attempts_left",
      },
    };

    try {
      await api.post(`industrial/customer/v1/questions/answers`, body, headers);

      setLoading(false);
      setInitialState(false);
      setValidatedQuestions(true);
      setQuestionsAsValid(true);
      toast.success("Todas as respostas estão corretas!");
    } catch (err) {
      setLoading(false);
      setInitialState(false);
      setValidatedQuestions(false);
      setAttemptsLeft(err.response.headers.attempts_left);

      if (err) {
        if (err.response.data.code === "422.051") {
          setHasAttemptsLeft(false);
        } else if (err.response.status === 422) {
          toast.error(err.response.data.message);
        }
      } else if (err.response.status !== 401) {
        toast.error("Ocorreu um erro interno na aplicação");
      }
    }
  };

  const tryAgain = (): void => {
    if (hasAttemptsLeft) {
      setAnswerInput("");
      setInitialState(true);
      setAnswers([]);
      setQuestionsPool([]);
      setValidatedQuestions(false);
      setQuestionCounter(0);
    } else {
      handleModal(false, false);
    }
  };

  const renderBody = useMemo(() => {
    if (initialState) {
      const label = `Sua resposta aqui${isState ? " (Ex. SP, RJ, BA)" : ""}`;
      return (
        <ContentButtons>
          <TextField
            name="answer"
            label={label}
            placeholder={label}
            value={answerInput}
            type={hasCep ? "numeric" : "text"}
            inputMode={hasCep ? "numeric" : "text"}
            onChange={handleInputChange}
          />
          <ContainedButton
            block
            type="button"
            continue
            width="100%"
            onClick={() => nextQuestion()}
          >
            {answers.length >= 1 ? "Enviar" : "Próximo"}
          </ContainedButton>
        </ContentButtons>
      );
    }

    if (validatedQuestions) {
      return (
        <Content>
          <AttemptsLeft>
            <IoIosCheckmarkCircleOutline size={84} color="#4FFB23" />
            <section>
              <p>Validado com sucesso!</p>
              <p>
                Clique em continuar para cadastrar um novo e-mail ou telefone.
              </p>
            </section>
          </AttemptsLeft>
          <ContainedButton
            block
            type="button"
            continue
            width="100%"
            onClick={() => handleModal(false, true)}
          >
            Continuar
          </ContainedButton>
        </Content>
      );
    }

    if (hasAttemptsLeft) {
      return (
        <Content>
          <AttemptsLeft>
            <IoIosCloseCircleOutline size={84} color="#F25A28" />
            <section>
              <p>Não foi possível continuar.</p>
              <p>Você precisa responder corretamente às perguntas!</p>
              <p>Você tem mais {attemptsLeft || 0} tentativas.</p>
            </section>
          </AttemptsLeft>
          <ContainedButton
            block
            type="button"
            continue
            width="100%"
            onClick={() => tryAgain()}
          >
            {attemptsLeft > 0 ? "Tente novamente" : "Fechar"}
          </ContainedButton>
        </Content>
      );
    }

    return (
      <Content>
        <AttemptsLeft>
          <IoIosCloseCircleOutline size={84} color="#F25A28" />
          <section>
            <p>
              Você foi bloqueado pois excedeu o número máximo de 5 de
              tentativas.
            </p>
            <p>Converse com nossos especialistas pelo Chat Online.</p>
            <p>
              Nosso time está conectado 24 horas por dia e 7 dias por semana
              para melhor atendê-lo.
            </p>
          </section>
        </AttemptsLeft>
      </Content>
    );
  }, [
    initialState,
    attemptsLeft,
    hasAttemptsLeft,
    validatedQuestions,
    answers,
    answerInput,
  ]);

  return (
    <PopupWrapper>
      <PopupContainer>
        <Header>
          <Title>Cadastro (Perguntas de validação)</Title>
          <ContentRight>
            {initialState && <Counter>{questionCounter + 1}/2</Counter>}
            <MdClose
              size={24}
              onClick={() => {
                if (validatedQuestions) {
                  handleModal(false, true);
                } else {
                  handleModal(false, false);
                }
              }}
            />
          </ContentRight>
        </Header>
        {initialState && (
          <Paragraph>
            Responda as perguntas corretamente para validar seu cadastro
            anterior
          </Paragraph>
        )}
        {initialState && <QuestionTitle>{currentQuestion}</QuestionTitle>}

        <AnswerForm
          initialState={initialState}
          onSubmit={(e) => e.preventDefault()}
        >
          {renderBody}
        </AnswerForm>
      </PopupContainer>
    </PopupWrapper>
  );
};

export default QuestionPool;
